import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Leaf, ArrowLeft, Printer, Download, Sprout, Sun, Droplets, Calendar
} from "lucide-react";

const SeedPacketTemplate = () => {
  const { user } = useAuth();
  const printRef = useRef(null);
  const [packetData, setPacketData] = useState({
    plant_name: "",
    variety: "",
    seed_count: "",
    year_harvested: new Date().getFullYear(),
    origin: "",
    growing_instructions: "",
    days_to_germination: "",
    planting_depth: "",
    spacing: "",
    sun_requirements: "Full Sun",
    seller_name: user?.name || "",
    is_organic: false,
    is_heirloom: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPacketData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>ROOTSPACE Seed Packet</title>
          <style>
            @page { size: 3.5in 5in; margin: 0; }
            body { margin: 0; padding: 0; font-family: 'Georgia', serif; }
            .packet { 
              width: 3.5in; 
              height: 5in; 
              padding: 0.25in;
              box-sizing: border-box;
              border: 2px dashed #ccc;
              position: relative;
              background: linear-gradient(135deg, #f8f5f0 0%, #fff 100%);
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid #2d5a27; 
              padding-bottom: 8px;
              margin-bottom: 8px;
            }
            .header img {
              height: 40px;
              width: auto;
              margin: 0 auto 4px;
            }
            .brand { 
              font-size: 14px; 
              font-weight: bold; 
              color: #2d5a27;
              letter-spacing: 2px;
            }
            .tagline { font-size: 8px; color: #666; }
            .plant-name { 
              font-size: 18px; 
              font-weight: bold; 
              color: #1a1a1a;
              margin: 8px 0 2px;
            }
            .variety { 
              font-size: 12px; 
              color: #666;
              font-style: italic;
            }
            .badges {
              display: flex;
              gap: 8px;
              justify-content: center;
              margin: 8px 0;
            }
            .badge {
              font-size: 8px;
              padding: 2px 6px;
              border-radius: 10px;
              background: #e8f5e9;
              color: #2d5a27;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 4px;
              font-size: 9px;
              margin: 8px 0;
            }
            .info-item {
              display: flex;
              align-items: center;
              gap: 4px;
            }
            .info-label { color: #666; }
            .info-value { font-weight: bold; color: #333; }
            .instructions {
              font-size: 8px;
              line-height: 1.4;
              color: #444;
              margin: 8px 0;
              padding: 6px;
              background: #f5f5f5;
              border-radius: 4px;
            }
            .instructions-title {
              font-weight: bold;
              font-size: 9px;
              margin-bottom: 4px;
              color: #2d5a27;
            }
            .footer {
              position: absolute;
              bottom: 0.25in;
              left: 0.25in;
              right: 0.25in;
              text-align: center;
              font-size: 8px;
              color: #888;
              border-top: 1px solid #ddd;
              padding-top: 6px;
            }
            .seed-count {
              font-size: 11px;
              font-weight: bold;
              color: #2d5a27;
              text-align: center;
              margin: 6px 0;
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/marketplace" className="text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-serif text-xl font-bold text-foreground">
              Seed Packet Designer
            </h1>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="RootSpace.Pro" className="h-8 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle className="font-serif">Packet Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plant Name</Label>
                  <Input
                    name="plant_name"
                    value={packetData.plant_name}
                    onChange={handleChange}
                    placeholder="Tomato"
                  />
                </div>
                <div>
                  <Label>Variety</Label>
                  <Input
                    name="variety"
                    value={packetData.variety}
                    onChange={handleChange}
                    placeholder="Cherokee Purple"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Seed Count</Label>
                  <Input
                    name="seed_count"
                    value={packetData.seed_count}
                    onChange={handleChange}
                    placeholder="25"
                  />
                </div>
                <div>
                  <Label>Year Harvested</Label>
                  <Input
                    name="year_harvested"
                    type="number"
                    value={packetData.year_harvested}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label>Origin</Label>
                  <Input
                    name="origin"
                    value={packetData.origin}
                    onChange={handleChange}
                    placeholder="Austin, TX"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Germination</Label>
                  <Input
                    name="days_to_germination"
                    value={packetData.days_to_germination}
                    onChange={handleChange}
                    placeholder="7-14 days"
                  />
                </div>
                <div>
                  <Label>Planting Depth</Label>
                  <Input
                    name="planting_depth"
                    value={packetData.planting_depth}
                    onChange={handleChange}
                    placeholder="1/4 inch"
                  />
                </div>
                <div>
                  <Label>Spacing</Label>
                  <Input
                    name="spacing"
                    value={packetData.spacing}
                    onChange={handleChange}
                    placeholder="24 inches"
                  />
                </div>
              </div>

              <div>
                <Label>Sun Requirements</Label>
                <select
                  name="sun_requirements"
                  value={packetData.sun_requirements}
                  onChange={handleChange}
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                >
                  <option>Full Sun</option>
                  <option>Partial Sun</option>
                  <option>Shade</option>
                </select>
              </div>

              <div>
                <Label>Growing Instructions</Label>
                <Textarea
                  name="growing_instructions"
                  value={packetData.growing_instructions}
                  onChange={handleChange}
                  placeholder="Sow seeds indoors 6-8 weeks before last frost. Transplant after danger of frost has passed..."
                  rows={4}
                />
              </div>

              <div>
                <Label>Seller Name</Label>
                <Input
                  name="seller_name"
                  value={packetData.seller_name}
                  onChange={handleChange}
                  placeholder="Your name or farm"
                />
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_organic"
                    checked={packetData.is_organic}
                    onChange={handleChange}
                    className="rounded"
                  />
                  <span className="text-sm">Organic</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_heirloom"
                    checked={packetData.is_heirloom}
                    onChange={handleChange}
                    className="rounded"
                  />
                  <span className="text-sm">Heirloom</span>
                </label>
              </div>

              <Button onClick={handlePrint} className="w-full btn-primary" data-testid="print-packet">
                <Printer className="w-4 h-4 mr-2" />
                Print Seed Packet
              </Button>
            </CardContent>
          </Card>

          {/* Preview */}
          <div>
            <h2 className="font-serif text-lg font-bold mb-4">Preview</h2>
            <div className="flex justify-center">
              <div 
                ref={printRef}
                className="w-[3.5in] h-[5in] bg-gradient-to-br from-stone-50 to-white border-2 border-dashed border-stone-300 rounded-lg p-4 relative"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                {/* Header */}
                <div className="text-center border-b-2 border-primary pb-2 mb-2">
                  <img src="/logo.png" alt="RootSpace.Pro" className="h-10 w-auto mx-auto mb-1" />
                  <p className="text-[8px] text-muted-foreground">Grow Your Own Paradise</p>
                </div>

                {/* Plant Name */}
                <h3 className="text-lg font-bold text-center text-foreground">
                  {packetData.plant_name || "Plant Name"}
                </h3>
                {packetData.variety && (
                  <p className="text-xs text-center text-muted-foreground italic">
                    {packetData.variety}
                  </p>
                )}

                {/* Badges */}
                <div className="flex gap-2 justify-center my-2">
                  {packetData.is_organic && (
                    <span className="text-[8px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                      Organic
                    </span>
                  )}
                  {packetData.is_heirloom && (
                    <span className="text-[8px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                      Heirloom
                    </span>
                  )}
                </div>

                {/* Seed Count */}
                <p className="text-center text-sm font-bold text-primary my-2">
                  {packetData.seed_count || "##"} Seeds
                </p>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-1 text-[9px] my-2">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Year:</span>
                    <span className="font-semibold">{packetData.year_harvested}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Sprout className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Germ:</span>
                    <span className="font-semibold">{packetData.days_to_germination || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Sun className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Sun:</span>
                    <span className="font-semibold">{packetData.sun_requirements}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Droplets className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Depth:</span>
                    <span className="font-semibold">{packetData.planting_depth || "N/A"}</span>
                  </div>
                </div>

                {/* Instructions */}
                {packetData.growing_instructions && (
                  <div className="bg-muted/50 p-2 rounded text-[8px] my-2">
                    <p className="font-bold text-[9px] text-primary mb-1">Growing Instructions</p>
                    <p className="text-muted-foreground leading-tight line-clamp-4">
                      {packetData.growing_instructions}
                    </p>
                  </div>
                )}

                {/* Footer */}
                <div className="absolute bottom-3 left-4 right-4 text-center border-t border-border pt-2">
                  <p className="text-[8px] text-muted-foreground">
                    Grown by {packetData.seller_name || "Gardener"} • {packetData.origin || "USA"}
                  </p>
                  <p className="text-[7px] text-muted-foreground/70 mt-0.5">
                    rootspace.pro
                  </p>
                </div>
              </div>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Print on 3.5&quot; x 5&quot; paper or cardstock
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SeedPacketTemplate;
